'use client';
import React, { useState, useRef, useEffect, useLayoutEffect, useSyncExternalStore } from 'react';
import { Card, Button } from '@ui';
import { SectionHeader } from '@layout';
import { type TabData } from './TabsData';
import { readPersistedTabIndex, writePersistedTabIndex } from './tabsPersistence';

export type { TabData } from './TabsData';
export type TabsVariant = 'default' | 'pills' | 'rounded';

/** Same breakpoint as `@include devices(mobile-max)` in `_tabs.scss` (max-width: 767px). */
const MOBILE_MAX_MQ = '(max-width: 767px)';

function subscribeMobileMax(onStoreChange: () => void) {
  if (typeof window === 'undefined') return () => {};
  const mq = window.matchMedia(MOBILE_MAX_MQ);
  const onChange = () => onStoreChange();
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}

function getMobileMaxSnapshot() {
  return typeof window !== 'undefined' && window.matchMedia(MOBILE_MAX_MQ).matches;
}

function getMobileMaxServerSnapshot() {
  return false;
}

interface TabsProps {
  tabs: TabData[];
  id?: string;
  className?: string;
  variant?: TabsVariant;
  persistKey?: string;
  /**
   * When false (default), only the active tab’s panel body is mounted. Saves work for heavy
   * panels (e.g. portfolio image grids). Set true if inactive tabs must stay mounted (forms,
   * media that should preload while hidden, etc.).
   */
  mountInactiveTabPanels?: boolean;
}

const Tabs = ({
  tabs,
  id = 'tabs1',
  className,
  variant = 'default',
  persistKey,
  mountInactiveTabPanels = false,
}: TabsProps) => {
  const [activeIdx, setActiveIdx] = useState(0);
  /** Mobile accordion: which panel is expanded (`null` = all collapsed). Selection stays in `activeIdx`. */
  const [mobileExpandedIdx, setMobileExpandedIdx] = useState<number | null>(0);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const hasInitializedPersistence = useRef(false);
  const pendingRestoredIdx = useRef<number | null>(null);
  const prevIsMobileMaxRef = useRef<boolean | null>(null);
  const safeActiveIdx = tabs.length > 0 ? Math.min(activeIdx, tabs.length - 1) : 0;
  const safeMobileExpandedIdx =
    mobileExpandedIdx !== null &&
    mobileExpandedIdx >= 0 &&
    mobileExpandedIdx < tabs.length &&
    tabs.length > 0
      ? mobileExpandedIdx
      : null;

  const isMobileMaxLayout = useSyncExternalStore(
    subscribeMobileMax,
    getMobileMaxSnapshot,
    getMobileMaxServerSnapshot
  );

  const persistActiveIndex = (idx: number) => {
    if (!persistKey) return;
    if (tabs.length === 0) return;
    try {
      writePersistedTabIndex(persistKey, idx);
    } catch (e) {
      // localStorage may be unavailable in private browsing modes
    }
  };

  useEffect(() => {
    if (!persistKey) return;
    if (tabs.length === 0) return;
    try {
      const parsed = readPersistedTabIndex(persistKey);
      if (parsed === null) return;
      if (Number.isInteger(parsed) && parsed >= 0 && parsed < tabs.length) {
        pendingRestoredIdx.current = parsed;
        setActiveIdx(parsed);
        setMobileExpandedIdx(parsed);
      }
    } catch (e) {
      // localStorage may be unavailable in private browsing modes
    } finally {
      hasInitializedPersistence.current = true;
    }
  }, [persistKey, tabs.length]);

  useEffect(() => {
    if (!persistKey) return;
    if (tabs.length === 0) return;
    if (!hasInitializedPersistence.current) return;
    if (pendingRestoredIdx.current !== null && activeIdx !== pendingRestoredIdx.current) return;
    if (pendingRestoredIdx.current === activeIdx) {
      pendingRestoredIdx.current = null;
    }
    persistActiveIndex(activeIdx);
  }, [activeIdx, persistKey, tabs.length]);

  useLayoutEffect(() => {
    const wasMobile = prevIsMobileMaxRef.current;
    prevIsMobileMaxRef.current = isMobileMaxLayout;
    if (isMobileMaxLayout && wasMobile === false) {
      setMobileExpandedIdx(activeIdx);
    }
  }, [isMobileMaxLayout, activeIdx]);

  useEffect(() => {
    if (
      mobileExpandedIdx !== null &&
      tabs.length > 0 &&
      (mobileExpandedIdx < 0 || mobileExpandedIdx >= tabs.length)
    ) {
      setMobileExpandedIdx(null);
    }
  }, [tabs.length, mobileExpandedIdx]);

  const handleTabClick = (idx: number) => {
    setActiveIdx(idx);
    tabRefs.current[idx]?.focus();
  };

  const handleMobileAccordionClick = (idx: number) => {
    if (idx !== safeActiveIdx) {
      setActiveIdx(idx);
      setMobileExpandedIdx(idx);
      return;
    }
    setMobileExpandedIdx((prev) => (prev === idx ? null : idx));
  };

  const handleKeyDown = (idx: number) => (e: React.KeyboardEvent<HTMLButtonElement>) => {
    const { key } = e;
    let newIdx = idx;
    if (key === 'ArrowRight') {
      newIdx = (idx + 1) % tabs.length;
      setActiveIdx(newIdx);
      tabRefs.current[newIdx]?.focus();
      e.preventDefault();
    } else if (key === 'ArrowLeft') {
      newIdx = (idx - 1 + tabs.length) % tabs.length;
      setActiveIdx(newIdx);
      tabRefs.current[newIdx]?.focus();
      e.preventDefault();
    } else if (key === 'Home') {
      setActiveIdx(0);
      tabRefs.current[0]?.focus();
      e.preventDefault();
    } else if (key === 'End') {
      setActiveIdx(tabs.length - 1);
      tabRefs.current[tabs.length - 1]?.focus();
      e.preventDefault();
    }
  };

  const showDesktopPanelBody = (idx: number) => mountInactiveTabPanels || safeActiveIdx === idx;
  const showMobilePanelBody = (idx: number) =>
    mountInactiveTabPanels || safeMobileExpandedIdx === idx;

  const variantClassName = variant === 'default' ? '' : ` tabs-${variant}`;

  const desktopLayout = (
    <div className='tabs-desktop'>
      <div
        className='tablist scrollable'
        role='tablist'
        aria-label='Example tabs'
        aria-activedescendant={tabs[safeActiveIdx]?.id}
      >
        {tabs.map((tab, idx) => (
          <Button
            key={tab.id}
            label={tab.label}
            className={`tab${safeActiveIdx === idx ? ' is-active' : ''}`}
            role='tab'
            id={tab.id}
            aria-label={tab.label}
            aria-selected={safeActiveIdx === idx}
            aria-controls={tab.panelId}
            aria-disabled={safeActiveIdx === idx ? undefined : true}
            tabIndex={0}
            ref={(el: HTMLButtonElement | null) => {
              tabRefs.current[idx] = el;
            }}
            onClick={() => handleTabClick(idx)}
            onKeyDown={handleKeyDown(idx)}
            type='button'
            as='button'
          />
        ))}
      </div>
      <Card className='tab-panels'>
        {tabs.map((tab, idx) => (
          <div
            key={tab.panelId}
            id={tab.panelId}
            className='panel'
            role='tabpanel'
            aria-labelledby={tab.id}
            aria-hidden={safeActiveIdx !== idx}
            // Panel is not tabbable; only tabs are in tab order
            hidden={safeActiveIdx !== idx}
          >
            {showDesktopPanelBody(idx) ? (
              <>
                {tab.title && <SectionHeader title={tab.title} description='' as='h2' />}
                {tab.content}
              </>
            ) : null}
          </div>
        ))}
      </Card>
    </div>
  );

  const mobileLayout = (
    <div className='tabs-mobile' role='presentation'>
      {tabs.map((tab, idx) => {
        const mobileTriggerId = `${tab.id}-m`;
        const mobilePanelId = `${tab.panelId}-m`;
        const isSelected = safeActiveIdx === idx;
        const isExpanded = safeMobileExpandedIdx === idx;
        return (
          <Card key={`${tab.id}-acc`} className='tabs-mobile-item'>
            <Button
              label={tab.label}
              className={`tabs-mobile-trigger${isSelected ? ' is-active' : ''}`}
              type='button'
              as='button'
              id={mobileTriggerId}
              aria-expanded={isExpanded}
              aria-controls={mobilePanelId}
              aria-current={isSelected ? 'true' : undefined}
              aria-label={tab.label}
              onClick={() => handleMobileAccordionClick(idx)}
            />

            <div
              id={mobilePanelId}
              className='tabs-mobile-panel'
              role='region'
              aria-labelledby={mobileTriggerId}
              hidden={!isExpanded}
            >
              {showMobilePanelBody(idx) ? (
                <div className='tabs-mobile-panel-inner'>
                  {tab.title && <SectionHeader title={tab.title} description='' as='h2' />}
                  {tab.content}
                </div>
              ) : null}
            </div>
          </Card>
        );
      })}
    </div>
  );

  return (
    <div className={`tabs${variantClassName}${className ? ` ${className}` : ''}`} data-tabs id={id}>
      {isMobileMaxLayout ? mobileLayout : desktopLayout}
    </div>
  );
};

export default Tabs;
